import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { getCsrfHeaders } from "@/lib/api/csrf";
import {
  Loader2,
  Save,
  User as UserIcon,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { localizedDictionaryMeta } from "@/lib/utils/seo";

export const Route = createFileRoute("/account/profile")({
  head: () => ({
    meta: localizedDictionaryMeta(
      "account.profile.title",
      "account.profile.subtitle",
      "Profile settings",
      "Update your company and contact details.",
    ),
  }),
  component: Profile,
});

function Profile() {
  const { t } = useI18n();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const passwordFormRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData) as Record<string, string>;

    const profileData = {
      name: data.name || user?.name,
      company: data.company || user?.company,
      email: data.email || user?.email,
      phone: data.phone || user?.phone,
      street: data.street || user?.street,
      city: data.city || user?.city,
      country: data.country || user?.country,
    };

    try {
      const response = await fetch("/api/portal/profile", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getCsrfHeaders(),
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        updateUser(profileData);
        toast.success(t("account.profile.toastSuccess"));
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to update profile. Please try again.");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error("Network error. Failed to connect to server.");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError(null);

    const formData = new FormData(e.currentTarget);
    const currentPassword = String(formData.get("currentPassword") || "");
    const newPassword = String(formData.get("newPassword") || "");
    const confirmPassword = String(formData.get("confirmPassword") || "");

    if (newPassword.length < 6) {
      setPasswordError(t("auth.validation.passwordMin"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("account.profile.passwordsDoNotMatch"));
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch("/api/portal/profile/password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getCsrfHeaders(),
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });

      const resData = await response.json();
      if (response.ok) {
        toast.success(t("account.profile.passwordUpdated"));
        passwordFormRef.current?.reset();
      } else {
        setPasswordError(
          resData.message || "Failed to update password. Please check your current password.",
        );
      }
    } catch (err) {
      console.error("Change password error:", err);
      setPasswordError("Network error. Please try again later.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="font-display text-3xl font-black text-navy">{t("account.profile.title")}</h1>
        <p className="mt-2 text-muted-foreground text-sm font-medium">
          {t("account.profile.subtitle")}
        </p>
      </div>

      {/* Partner Info Card */}
      <div className="glass-card rounded-2xl border border-border/60 p-6 sm:p-8 max-w-2xl shadow-sm">
        <form onSubmit={handleSubmit} className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2 flex items-center gap-3 pb-4 border-b border-border/40">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary border border-primary/20">
              <UserIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-bold text-navy text-sm uppercase tracking-wider">
                {t("account.profile.section.partnerInfo")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("account.profile.section.partnerDesc")}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="name" className="text-xs font-bold text-navy uppercase tracking-wider">
              {t("account.profile.fullName")}
            </Label>
            <Input
              id="name"
              name="name"
              defaultValue={user?.name}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
              required
            />
          </div>
          <div>
            <Label
              htmlFor="company"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.company")}
            </Label>
            <Input
              id="company"
              name="company"
              defaultValue={user?.company}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
              required
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-bold text-navy uppercase tracking-wider">
              {t("account.profile.email")}
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              defaultValue={user?.email}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
              required
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-xs font-bold text-navy uppercase tracking-wider">
              {t("account.profile.phone")}
            </Label>
            <Input
              id="phone"
              name="phone"
              defaultValue={user?.phone ?? ""}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <Label
              htmlFor="street"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.street")}
            </Label>
            <Input
              id="street"
              name="street"
              defaultValue={user?.street ?? ""}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>
          <div>
            <Label htmlFor="city" className="text-xs font-bold text-navy uppercase tracking-wider">
              {t("account.profile.city")}
            </Label>
            <Input
              id="city"
              name="city"
              defaultValue={user?.city ?? ""}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>
          <div>
            <Label
              htmlFor="country"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.country")}
            </Label>
            <Input
              id="country"
              name="country"
              defaultValue={user?.country ?? ""}
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>

          <Button
            className="sm:col-span-2 justify-self-start mt-2 rounded-xl font-bold px-6 py-2.5 shadow-sm inline-flex items-center gap-2 cursor-pointer transition-all hover:scale-[1.01]"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("account.profile.saving")}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t("account.profile.save")}
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Security & Password Card */}
      <div className="glass-card rounded-2xl border border-border/60 p-6 sm:p-8 max-w-2xl shadow-sm">
        <form
          ref={passwordFormRef}
          onSubmit={handlePasswordSubmit}
          className="grid gap-5 sm:grid-cols-2"
        >
          <div className="sm:col-span-2 flex items-center gap-3 pb-4 border-b border-border/40">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary border border-primary/20">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <div className="font-bold text-navy text-sm uppercase tracking-wider">
                {t("account.profile.section.security")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("account.profile.section.securityDesc")}
              </div>
            </div>
          </div>

          {passwordError && (
            <div className="sm:col-span-2 flex items-start gap-2.5 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs leading-relaxed animate-in fade-in-0">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{passwordError}</span>
            </div>
          )}

          <div className="sm:col-span-2">
            <Label
              htmlFor="currentPassword"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.currentPassword")}
            </Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              placeholder="••••••••"
              required
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>

          <div>
            <Label
              htmlFor="newPassword"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.newPassword")}
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                placeholder="••••••••"
                required
                className="pr-10 rounded-xl border-border bg-white shadow-sm"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                aria-label={showNewPassword ? t("auth.password.hide") : t("auth.password.show")}
                className="absolute end-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:text-navy transition-colors cursor-pointer"
              >
                {showNewPassword ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <Label
              htmlFor="confirmPassword"
              className="text-xs font-bold text-navy uppercase tracking-wider"
            >
              {t("account.profile.confirmPassword")}
            </Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showNewPassword ? "text" : "password"}
              placeholder="••••••••"
              required
              className="mt-1.5 rounded-xl border-border bg-white shadow-sm"
            />
          </div>

          <Button
            className="sm:col-span-2 justify-self-start mt-2 rounded-xl font-bold px-6 py-2.5 shadow-sm inline-flex items-center gap-2 cursor-pointer transition-all hover:scale-[1.01]"
            type="submit"
            disabled={savingPassword}
          >
            {savingPassword ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("account.profile.saving")}
              </>
            ) : (
              <>
                <KeyRound className="h-4 w-4" />
                {t("account.profile.updatePassword")}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
